<script>
import { mapState, mapActions, mapGetters } from "vuex";

import InfoTooltip from "@/components/InfoTooltip";
import AutoRefreshToggle from "@/components/AutoRefreshToggle";
import HashrateChart from "@/components/HashrateChart";
import NavigationWrapper from "@/components/NavigationWrapper";
import {
  formatHashrate,
  formatRelativeDate,
  formatPool,
  getBreakpoint,
} from "@/utils";
import DeleteWorkerModal from "@/pages/Workers/Worker/DeleteModal";

export default {
  // Included components
  components: {
    NavigationWrapper,
    HashrateChart,
    InfoTooltip,
    DeleteWorkerModal,
    AutoRefreshToggle,
  },
  data() {
    return {
      // The ID of the worker being displayed
      workerId: null,
      hoverHeading: false,
    };
  },

  computed: {
    // The raw share data from the server
    shares() {
      return this.workerShares[this.workerId];
    },

    // The hashrate data for the worker
    hashrate() {
      if (this.shares == null) return 0;
      return this.shares.map((share) => [
        share[0],
        share[1] / 300, // 5 minutes
      ]);
    },

    // The actual worker
    worker() {
      return this.workers[this.workerId];
    },

    workerNotes() {
      var notes = this.workers[this.workerId].notes;
      var editedNotes = notes.replace(/\n/g, " <br> ");
      return editedNotes;
    },

    // Mapped vuex state
    ...mapGetters(["can"]),
    ...mapState(["pools", "workers", "workerShares", "activeTeamId"]),
  },

  // Initialize the state of the component
  async created() {
    this.workerId = this.$route.params.workerId;

    // Get this current worker
    await this.getWorker({ workerId: this.workerId });

    // If this worker's team ID is not the active team ID, then go back to workers page
    if (this.worker.team_id !== this.activeTeamId) {
      await this.$router.push("/workers");
      return;
    }

    // Parallelize fetch worker information
    await Promise.all([
      this.getWorkerShares({
        workerId: this.workerId,
      }),
      this.getPools(),
    ]);
  },

  methods: {
    // Update the hashrate chart data (every 5 minutes if autorefresh is on)
    updateHashrateChartData() {
      this.getWorkerShares({
        workerId: this.workerId,
      });
    },

    // Update the worker statistics (every 30 seconds if autorefresh is on)
    updateStatistics() {
      this.getWorker({ workerId: this.workerId });
    },

    // Helper methods and mapped vuex actions
    formatHashrate,
    formatRelativeDate,
    formatPool,
    getBreakpoint,
    ...mapActions([
      "getWorker",
      "getPools",
      "getWorkerShares",
      "getWorkerHashrate",
      "getWorkerLastSubmission",
      "getWorkerFirstSubmission",
    ]),
  },

  filters: {
    truncate(label) {
      if (label.length >= 25) {
        return (
          label.substr(0, 10) + "..." + label.substr(label.length - 10, 10)
        );
      } else {
        return label;
      }
    },
  },
};
</script>

<template>
  <NavigationWrapper>
    <template #breadcrumbs>
      <b-breadcrumb-item to="/workers">Workers</b-breadcrumb-item>
      <b-breadcrumb-item active>
        {{ worker != null ? worker.name : "Worker" }}
      </b-breadcrumb-item>
    </template>
    <b-row class="mb-3">
      <b-col cols="7">
        <h1>{{ worker.name }}</h1>
        <span
          v-if="worker.pool_configuration"
          @mouseover="hoverHeading = true"
          @mouseleave="hoverHeading = false"
        >
          <h4 v-if="!hoverHeading" class="Worker-pool-name">
            {{
              worker.pool_configuration
                ? worker.pool_configuration.worker_name
                : "" | truncate
            }}
          </h4>
          <h4 v-if="hoverHeading">
            {{ worker.pool_configuration.worker_name }}
          </h4>
        </span>
        <AutoRefreshToggle
          v-if="getBreakpoint().xs || getBreakpoint().sm"
          @update-5m="updateHashrateChartData()"
          @update-30s="updateStatistics()"
        />
      </b-col>
      <b-col v-if="!getBreakpoint().xs" cols="5" align-self="center">
        <div class="float-right">
          <AutoRefreshToggle
            v-if="!(getBreakpoint().xs || getBreakpoint().sm)"
            @update-5m="updateHashrateChartData()"
            @update-30s="updateStatistics()"
          />
          <span v-if="can.workers.edit">
            <b-button class="ml-3 mr-3" variant="danger" v-b-modal.delete-modal>
              Delete
            </b-button>
            <DeleteWorkerModal :worker-id="workerId" modal-id="delete-modal" />
            <b-button variant="primary" :to="'/workers/' + worker.id + '/edit'">
              Edit
            </b-button>
          </span>
        </div>
      </b-col>
      <b-col v-else cols="5" class="text-right">
        <b-dropdown class="mt-2" text="Actions" variant="primary" size="sm">
          <b-dropdown-item v-b-modal.delete-modal>Delete</b-dropdown-item>
          <DeleteWorkerModal :worker-id="workerId" modal-id="delete-modal" />
          <b-dropdown-item :to="'/workers/' + worker.id + '/edit'">
            Edit
          </b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col cols="12" class="Worker-device">
        {{ worker.device ? worker.device.name : "" }}
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-card class="mb-3">
          <b-row>
            <b-col sm="6">
              <div>
                <b>
                  Full Username
                  <InfoTooltip
                    text="This is the username miners should use to connect to the mine.app stratum"
                  />
                </b>
                {{ worker.team.handle }}.{{ worker.handle }}
              </div>
              <div>
                <b>
                  Hashrate
                  <InfoTooltip text="30 minute average hashrate" />
                </b>
                {{ formatHashrate(worker.hashrate_30m) }}
              </div>
              <div>
                <b>First Submission</b>
                {{ formatRelativeDate(worker.first_submitted) }}
              </div>
              <div>
                <b>Last Submission</b>
                {{ formatRelativeDate(worker.last_submitted) }}
              </div>
            </b-col>
            <b-col sm="6">
              <div v-if="worker.pool_configuration">
                <span
                  v-if="worker.pool_configuration.worker_name.length >= 25"
                  v-b-tooltip.hover="worker.pool_configuration.worker_name"
                >
                  <b>Pool Worker Name</b>
                  {{
                    worker.pool_configuration
                      ? worker.pool_configuration.worker_name
                      : "" | truncate
                  }}
                </span>
                <span v-if="worker.pool_configuration.worker_name.length < 25">
                  <b>Pool Worker Name</b>
                  {{
                    worker.pool_configuration
                      ? worker.pool_configuration.worker_name
                      : "" | truncate
                  }}
                </span>
              </div>
              <div v-if="!worker.pool_configuration">
                <span>
                  <b>Pool Worker Name</b>
                  None
                </span>
              </div>
              <div v-if="worker.pool_configuration">
                <b>Pool</b>
                {{
                  worker.pool_configuration
                    ? formatPool(
                        pools.find((pool) => {
                          return pool.id === worker.pool_configuration.pool_id;
                        })
                      )
                    : ""
                }}
              </div>
              <div v-if="!worker.pool_configuration">
                <b>Pool</b>
                None
              </div>
              <div>
                <b>Worker serial number</b>
                {{ worker.serial_number ? worker.serial_number : "—" }}
              </div>
            </b-col>
          </b-row>
        </b-card>

        <b-card no-body class="mb-3">
          <b-card-body class="p-2 p-md-3">
            <HashrateChart
              :axis-titles="false"
              :data="hashrate"
              :tick-amount="5"
              :canExport="true"
            />
          </b-card-body>
        </b-card>
        <b-row class="mb-3" v-if="worker.notes">
          <b-col>
            <h3 class="mb-3">Notes</h3>
            <b-card class="mb-3" v-if="worker.notes">
              <b-row>
                <b-col>
                  <div style="white-space: pre-line">{{ worker.notes }}</div>
                </b-col>
              </b-row>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </NavigationWrapper>
</template>

<style scoped>
.Worker-stat-value {
  font-size: 1.4rem;
}

.Worker-device {
  color: #6c757d;
}

.Worker-pool-name {
  color: #3b4044;
}

.InfoTooltip-icon {
  display: inline-block;
  color: rgba(0, 0, 0, 0.45);
  padding: 0.12rem 0.5rem;
  margin: 0 -0.5rem;
  height: 24px;
}
</style>
